import { useState, type FormEvent } from "react";
import SectionHeading from "@/components/feature/SectionHeading";

const FORM_URL = "https://readdy.ai/api/form/da2lk5qamq5jpe1hrvvg";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("phone_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("phone_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams(fd as unknown as Record<string, string>).toString();
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        (parsed?.meta?.message as string) ||
        (parsed?.message as string) ||
        (parsed?.meta?.detail as string) ||
        responseText;
      const okCode = parsed?.code === "OK";
      const isSpam = /spam|form data is spam/i.test(serverMsg || "");

      if (res.ok && okCode && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("idle");
        setFormError(
          isSpam
            ? "提交内容未能通过校验，请调整后重新提交。"
            : (serverMsg && `提交失败：${serverMsg}`) || "提交失败，请稍后再试。",
        );
      }
    } catch {
      setStatus("idle");
      setFormError("网络异常，请检查后重试。");
    }
  };

  return (
    <section id="contact" className="scroll-mt-16 bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="我们随时欢迎你的建议"
          title="联系我们"
          subtitle="有想看的作品、对分类的建议，或发现了译文问题？告诉我们，帮助我们把动漫之家做得更好。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="flex flex-col gap-4 lg:col-span-2">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                <i className="ri-customer-service-2-line text-primary-600"></i>
                读者服务
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                遇到追更、反馈或内容相关的问题，欢迎来信。我们会在一个工作日内回复。
              </p>
              <div className="mt-5 flex flex-col gap-3 text-sm text-foreground-600">
                <p className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-600"></i>
                  service@dmzjweb.example
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-time-line text-primary-600"></i>
                  周一至周日 9:00 – 21:00
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                <i className="ri-team-line text-primary-600"></i>
                汉化组合作
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                优秀的汉化组想加入独家专区？欢迎邮件洽谈合作：
              </p>
              <p className="mt-3 flex items-center gap-2 text-sm text-foreground-600">
                <i className="ri-mail-send-line text-primary-600"></i>
                partner@dmzjweb.example
              </p>
            </div>
          </div>

          <form
            data-readdy-form="动漫之家漫画网联系与建议表单"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8 lg:col-span-3"
          >
            <h3 className="text-base font-bold text-foreground-900">给我们留言</h3>
            <p className="mt-1 text-sm text-foreground-500">留下你的昵称与邮箱，方便我们回复你。</p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  昵称 / 称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  maxLength={30}
                  placeholder="怎么称呼你"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-700">
                反馈类型
              </label>
              <select
                id="topic"
                name="topic"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="作品推荐">作品推荐</option>
                <option value="分类建议">分类建议</option>
                <option value="译文问题反馈">译文问题反馈</option>
                <option value="追更问题">追更问题</option>
                <option value="其他建议">其他建议</option>
              </select>
            </div>

            <div className="mt-4">
              <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                留言内容
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="写下你的建议、想看的作品或遇到的问题（最多 500 字）"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />

            {formError && (
              <div className="mt-4 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <i className="ri-error-warning-line mr-1.5"></i>
                {formError}
              </div>
            )}
            {status === "success" && (
              <div className="mt-4 rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-line mr-1.5 text-primary-600"></i>
                已收到你的留言，感谢支持！我们会尽快回复你。
              </div>
            )}

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-6 inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting" ? "提交中…" : "提交留言"}
              {status !== "submitting" && <i className="ri-send-plane-line ml-2"></i>}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}